import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { MongooseFields } from '../../types'

/**
 * @class ApiService
 * @classdesc A service class for making API requests using Axios.
 */
export class ApiService {
  /**
   * @private
   * @readonly
   * @type {AxiosInstance}
   */
  public axios: AxiosInstance

  /**
   * Creates an instance of ApiService.
   * @param {AxiosInstance} instance - The Axios instance to be used for requests.
   * @throws {Error} If the Axios instance is not provided.
   */
  constructor(instance: AxiosInstance) {
    if (!instance) throw new Error('Axios instance is required')
    this.axios = instance
  }

  /**
   * Fetches a list of items from the given URL.
   * @template TData
   * @param {string} url - The URL to fetch the list from.
   * @param {Object} [params] - Optional parameters to include in the request.
   * @param {AxiosRequestConfig} [config] - Optional configuration for the request.
   * @returns {Promise<TData[]>} A promise that resolves to the list of items.
   */
  public getList = async <TData = any>(
    url: string,
    params?: any,
    config?: Omit<AxiosRequestConfig, 'params'>
  ): Promise<TData[]> => {
    const { data } = await this.axios.get<TData[]>(url, {
      params: {
        ...(params || {}),
      },
      ...config,
    } as AxiosRequestConfig)
    return data
  }

  /**
   * Fetches a paginated list of items from the given URL.
   * @template TData
   * @param {string} url - The URL to fetch the paginated list from.
   * @param {Object} [params] - Optional parameters to include in the request.
   * @param {AxiosRequestConfig} [config] - Optional configuration for the request.
   * @returns {Promise<{data: TData[], total: number}>} A promise that resolves to an object containing the list of items and the total count.
   */
  public getPaginatedList = async <TData = any>(
    url: string,
    params?: any,
    config?: Omit<AxiosRequestConfig, 'params'>
  ): Promise<{ data: TData[]; total: number }> => {
    const { data, headers } = await this.axios.get<TData[]>(url, {
      params: {
        ...(params || {}),
        count: true,
      },
      ...config,
    } as AxiosRequestConfig)
    return {
      data,
      total: Number(headers['odin-count']),
    }
  }

  /**
   * Fetches a single item by ID.
   * @template TData
   * @param {string} entity - The entity type to fetch.
   * @param {string} id - The ID of the item to fetch.
   * @param {AxiosRequestConfig} [config] - Optional configuration for the request.
   * @returns {Promise<TData>} A promise that resolves to the fetched item.
   */
  public getItem = async <TData = any>(entity: string, id: string, config?: AxiosRequestConfig): Promise<TData> => {
    const { data } = await this.axios.get<TData>(`${entity}/${id}`, config)
    return data
  }

  /**
   * Creates a new item.
   * @template TData
   * @returns {Promise<TData>} A promise that resolves to the created item.
   */
  public createItem = async <TData = any>(
    entity: string,
    data: Partial<Omit<TData, MongooseFields>>,
    config?: AxiosRequestConfig
  ): Promise<TData> => {
    const { data: response } = await this.axios.post<TData>(
      entity,
      {
        ...data,
      },
      config
    )
    return response
  }

  /**
   * Creates a new item with file upload.
   */
  public createUpload = async <TData = any>(
    entity: string,
    data: FormData,
    config?: Partial<AxiosRequestConfig>
  ): Promise<TData> => {
    const { data: response } = await this.axios.post<TData>(entity, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...(config || {}),
    })
    return response
  }

  /**
   * Updates an existing item.
   */
  public updateItem = async <TData = any>(
    entity: string,
    entityId: string,
    data: Partial<Omit<TData, MongooseFields>>,
    config?: AxiosRequestConfig
  ): Promise<TData> => {
    const { data: response } = await this.axios.put<TData>(
      `${entity}/${entityId}`,
      {
        ...data,
      },
      config
    )
    return response
  }

  /**
   * Fetches the total count of items from the given entity.
   * @template TData
   * @param {string} entity - The entity type to count.
   * @param {Object} [params] - Optional parameters to include in the request.
   * @param {AxiosRequestConfig} [config] - Optional configuration for the request.
   * @returns {Promise<number>} A promise that resolves to the total count of items.
   */
  public getListCount = async <TData = any>(
    entity: string,
    params?: any,
    config?: Omit<AxiosRequestConfig, 'params'>
  ): Promise<number> => {
    const { total } = await this.getPaginatedList<TData>(
      entity,
      {
        ...params,
        limit: 1,
      },
      config
    )
    return total
  }

  /**
   * Deletes an item by ID.
   * @param {string} entity - The entity type to delete.
   * @param {string} entityId - The ID of the item to delete.
   * @returns {Promise<void>} A promise that resolves when the item is deleted.
   */
  public deleteItem = async (entity: string, entityId: string): Promise<void> => {
    await this.axios.delete(`${entity}/${entityId}`)
  }
}
